import React, { useState, useEffect } from 'react';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { getFirestore, collection, addDoc, query, where, getDocs, orderBy } from 'firebase/firestore';
import { auth } from '../firebase';

const db = getFirestore();

const NewsSynthesizer = ({ setCurrentPage }) => {
  const [url, setUrl] = useState('');
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [storedAnalyses, setStoredAnalyses] = useState([]);
  const [expandedAnalysis, setExpandedAnalysis] = useState(null);

  useEffect(() => {
    fetchStoredAnalyses();
  }, []);

  const fetchStoredAnalyses = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const q = query(
        collection(db, "newsAnalysis"),
        where("userId", "==", user.uid),
        orderBy("timestamp", "desc")
      );

      const querySnapshot = await getDocs(q);
      const analyses = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setStoredAnalyses(analyses);
    } catch (error) {
      console.error("Error fetching stored analyses:", error);
      setError(`Failed to fetch stored analyses: ${error.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setResult(null);

    const webhookUrl = 'https://hook.eu2.make.com/lcwb8g8v2rgepk7bm097cuqspdws58ye';

    try {
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Custom-Header': 'NewsSynthesizer',
        },
        body: JSON.stringify({ url: url }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const processedData = {
        overview: data.overview,
        virality: data.virality,
        liner: data.liner,
      };

      setResult(processedData);

      try {
        const user = auth.currentUser;
        if (!user) {
          throw new Error('User not authenticated');
        }

        const docRef = await addDoc(collection(db, "newsAnalysis"), {
          userId: user.uid,
          url: url,
          ...processedData,
          timestamp: new Date()
        });
        console.log("Document written with ID: ", docRef.id);
        fetchStoredAnalyses(); // Refresh the list after adding a new analysis
      } catch (firestoreError) {
        console.error("Error adding document to Firestore: ", firestoreError);
        setError(`Failed to store result in database: ${firestoreError.message}`);
      }

    } catch (e) {
      console.error("There was a problem with the fetch operation:", e);
      setError(`An error occurred: ${e.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAnalysis = (id) => {
    setExpandedAnalysis(expandedAnalysis === id ? null : id);
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      <button onClick={() => setCurrentPage('home')} className="mb-4 flex items-center text-[rgb(100,204,201)] hover:underline">
        <ArrowLeft className="mr-2" /> Back to Dashboard
      </button>
      <h2 className="text-2xl font-bold mb-4">News Synthesizer</h2>
      <form onSubmit={handleSubmit} className="mb-6">
        <div className="mb-4">
          <label htmlFor="url" className="block text-sm font-medium text-gray-700">News Article URL</label>
          <input
            type="url"
            id="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[rgb(100,204,201)] focus:ring focus:ring-[rgb(100,204,201)] focus:ring-opacity-50"
            required
          />
        </div>
        <button 
          type="submit" 
          className="bg-[rgb(100,204,201)] text-white py-2 px-4 rounded hover:bg-[rgb(90,184,181)] transition-colors"
          disabled={isLoading}
        >
          {isLoading ? 'Analyzing...' : 'Analyze'}
        </button>
      </form>
      {isLoading && (
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[rgb(100,204,201)]"></div>
        </div>
      )}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      )}
      {result && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <h3 className="text-xl font-semibold mb-2">Latest Analysis Result</h3>
          <div className="mb-4">
            <h4 className="font-medium">Overview:</h4>
            {result.overview.split(/(?=🌐|🔍|💡)/).map((section, index) => (
              <div key={index} className="mb-3">
                {section.trim()}
              </div>
            ))}
          </div>
          <div className="mb-4">
            <h4 className="font-medium">One-liner:</h4>
            <p>{result.liner}</p>
          </div>
          <div>
            <h4 className="font-medium">Virality Score:</h4>
            <p>{result.virality}/100</p>
          </div>
        </div>
      )}
      <div>
        <h3 className="text-xl font-semibold mb-4">Stored Analyses</h3>
        {storedAnalyses.map((analysis) => (
          <div key={analysis.id} className="mb-4 border rounded-lg overflow-hidden">
            <div
              className="flex justify-between items-center p-4 bg-gray-100 cursor-pointer"
              onClick={() => toggleAnalysis(analysis.id)}
            >
              <h4 className="font-medium">{analysis.url}</h4>
              {expandedAnalysis === analysis.id ? <ChevronUp /> : <ChevronDown />}
            </div>
            {expandedAnalysis === analysis.id && (
              <div className="p-4">
                <div className="mb-2">
                  <h5 className="font-medium">Overview:</h5>
                  {analysis.overview.split(/(?=🌐|🔍|💡)/).map((section, index) => (
                    <div key={index} className="mb-3">
                      {section.trim()}
                    </div>
                  ))}
                </div>
                <div className="mb-2">
                  <h5 className="font-medium">One-liner:</h5>
                  <p>{analysis.liner}</p>
                </div>
                <div>
                  <h5 className="font-medium">Virality Score:</h5>
                  <p>{analysis.virality}/100</p>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default NewsSynthesizer;